import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useHomeBanners, bannerHref } from "@/hooks/useHomeBanners";

const AUTOPLAY_MS = 4000;
const SWIPE_THRESHOLD = 40;
const PRELOAD_NEIGHBOURS = 1; // only render N slides ahead/behind for performance

/**
 * Production-grade Hero Banner Carousel for the Home Page.
 * - Fixed 16:9 aspect ratio for ALL slides — consistent height on every page
 * - Images use object-cover to fill the box uniformly (mild crop on edges if image isn't 16:9)
 * - Auto-slides every 4s, pauses on hover/touch
 * - Touch swipe (mobile) + Prev/Next arrows (desktop) + dot indicators
 * - Lazy-renders only the active slide + 1 neighbour each side (handles 20+ banners smoothly)
 * - Images use loading="lazy" + decoding="async" — only the first slide is eager
 */
export default function HomeBannerCarousel() {
  const { banners, loading } = useHomeBanners();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  const total = banners.length;
  const go = useCallback((i) => {
    if (total === 0) return;
    setIndex(((i % total) + total) % total);
  }, [total]);
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    if (total < 2 || paused) return undefined;
    const t = setInterval(() => setIndex(i => (i + 1) % total), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [total, paused]);

  if (loading && total === 0) {
    return (
      <div className="w-full aspect-[16/9] rounded-2xl bg-amethyst-100 animate-pulse" aria-label="Loading banners" />
    );
  }
  if (total === 0) return null;

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; setPaused(true); };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > SWIPE_THRESHOLD) prev();
    else if (dx < -SWIPE_THRESHOLD) next();
    touchStartX.current = null;
    setTimeout(() => setPaused(false), 800);
  };

  const isVisible = (i) => {
    if (total <= 3) return true;
    const distLeft  = (index - i + total) % total;
    const distRight = (i - index + total) % total;
    return Math.min(distLeft, distRight) <= PRELOAD_NEIGHBOURS;
  };

  return (
    <div
      className="relative w-full aspect-[16/9] overflow-hidden rounded-2xl shadow-lg group bg-amethyst-100"
      data-testid="home-banner-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="region"
      aria-roledescription="carousel"
      aria-label="Home banners"
    >
      {banners.map((b, i) => (
        <Slide
          key={b.id || i}
          banner={b}
          active={i === index}
          eager={i === 0}
          shouldRender={isVisible(i)}
        />
      ))}

      {total > 1 && (
        <>
          <button
            type="button" onClick={prev} aria-label="Previous banner" data-testid="home-banner-prev"
            className="hidden sm:flex items-center justify-center absolute top-1/2 -translate-y-1/2 left-3 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-amethyst-900 shadow-lg opacity-0 group-hover:opacity-100 transition z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button" onClick={next} aria-label="Next banner" data-testid="home-banner-next"
            className="hidden sm:flex items-center justify-center absolute top-1/2 -translate-y-1/2 right-3 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-amethyst-900 shadow-lg opacity-0 group-hover:opacity-100 transition z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="absolute bottom-2 sm:bottom-3 left-0 right-0 flex items-center justify-center gap-1.5 z-10">
            {banners.map((_, i) => (
              <button
                key={i} type="button" onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index}
                data-testid={`home-banner-dot-${i}`}
                className={`transition-all rounded-full ${i === index ? "bg-white w-6 h-1.5" : "bg-white/60 hover:bg-white w-1.5 h-1.5"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Slide({ banner, active, eager, shouldRender }) {
  const link = bannerHref(banner);
  const isExternal = banner.link_type === "external";
  const hasOverlay = !!(banner.title || banner.subtitle || banner.cta_label);

  if (!shouldRender) {
    return <div className={`absolute inset-0 transition-opacity duration-700 ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`} />;
  }

  const Img = (
    <>
      {/* Soft background fill for non-16:9 images — clean, no visible blur bars */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-amethyst-50/60 to-white" aria-hidden="true" />
      {/* Actual image — contained so the WHOLE banner is visible (no crop) */}
      <img
        src={banner.image_url}
        alt={banner.title || "banner"}
        className="relative block w-full h-full object-contain"
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : "low"}
      />
    </>
  );

  const Overlay = hasOverlay ? (
    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/35 to-transparent flex items-end px-4 sm:px-8 md:px-12 pb-5 sm:pb-7 pt-10">
      <div className="text-white max-w-md sm:max-w-lg">
        {banner.title && (
          <h2 className="font-serif text-lg sm:text-2xl md:text-3xl font-semibold leading-tight drop-shadow">{banner.title}</h2>
        )}
        {banner.subtitle && (
          <p className="text-xs sm:text-sm md:text-base text-white/90 mt-1 drop-shadow line-clamp-2">{banner.subtitle}</p>
        )}
        {banner.cta_label && (
          <span className="inline-block mt-3 text-xs sm:text-sm px-4 py-1.5 rounded-full bg-magenta-500 hover:bg-magenta-600 transition text-white font-semibold shadow-md">
            {banner.cta_label} →
          </span>
        )}
      </div>
    </div>
  ) : null;

  const wrapperClass = `absolute inset-0 transition-opacity duration-700 ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`;

  if (!link) return <div className={wrapperClass}>{Img}{Overlay}</div>;

  return isExternal ? (
    <a href={link} target="_blank" rel="noreferrer" className={wrapperClass} data-testid={`home-banner-link-${banner.id}`}>
      {Img}{Overlay}
    </a>
  ) : (
    <a href={link} className={wrapperClass} data-testid={`home-banner-link-${banner.id}`}>
      {Img}{Overlay}
    </a>
  );
}
